import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    TextField,
    Typography
} from "@mui/material";
import { useEffect, useState } from "react";

const ReplyItem = ({ reply, fileId, onEdit, onDelete, editLoading }) => {
    const [editing, setEditing] = useState(false);
    const [comment, setComment] = useState(reply?.comment || "");

    useEffect(() => {
        setComment(reply?.comment || "");
    }, [reply?.comment]);

    return (
        <Box py={1}>
            <Typography variant="body2" color="text.secondary">
                {reply?.user?.name || "Usuario"} respondeu
                {reply?.accepted ? " - aprovado" : " - pendente"}
            </Typography>
            {editing ? (
                <Box mt={1}>
                    <TextField
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                    />
                    <Box mt={1} display="flex" gap={1} justifyContent="flex-end">
                        <Button onClick={() => setEditing(false)}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            disabled={!comment || editLoading}
                            onClick={() => {
                                onEdit({
                                    id: reply?.id,
                                    fileId,
                                    comment
                                });
                                setEditing(false);
                            }}
                        >
                            Salvar resposta
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {reply?.comment}
                </Typography>
            )}
            {!editing && (
                <Box mt={1} display="flex" gap={1}>
                    <Button size="small" onClick={() => setEditing(true)}>
                        Alterar resposta
                    </Button>
                    <Button
                        size="small"
                        color="error"
                        onClick={() => onDelete(reply?.id)}
                    >
                        Excluir resposta
                    </Button>
                </Box>
            )}
        </Box>
    );
};

const CommentModal = (props) => {

    const {
        comment,
        accepted,
        replies = [],
        user,
        lesson,
        fileId,
        onClose = () => {},
        onAccept = () => {},
        onDelete = () => {},
        onEdit = () => {},
        onReply = () => {},
        editLoading = false,
        replyLoading = false,
        ...others
    } = props;

    const [editComment, setEditComment] = useState(comment || "");
    const [localAccepted, setLocalAccepted] = useState(accepted);
    const [localReplies, setLocalReplies] = useState(replies || []);
    const [reply, setReply] = useState("");

    useEffect(() => {
        setEditComment(comment || "");
        setLocalAccepted(accepted);
        setLocalReplies(replies || []);
        setReply("");
    }, [accepted, comment, others.open, replies]);

    const submitReply = () => {
        const replyText = reply;
        const temporaryReplyId = `temporary-${Date.now()}`;
        const temporaryReply = {
            id: temporaryReplyId,
            comment: replyText,
            accepted: true,
            user: {
                name: "Admin"
            }
        };

        setLocalReplies((currentReplies) => [
            ...currentReplies,
            temporaryReply
        ]);
        setReply("");

        onReply({ comment: replyText, accepted: true, adminReply: true }, {
            onSuccess: (data) => {
                const newReply = data?.comment?.data || data?.comment;
                if (newReply) {
                    setLocalReplies((currentReplies) =>
                        currentReplies.map((replyItem) =>
                            replyItem?.id === temporaryReplyId
                                ? newReply
                                : replyItem
                        )
                    );
                }
            },
            onError: () => {
                setLocalReplies((currentReplies) =>
                    currentReplies.filter(
                        (replyItem) => replyItem?.id !== temporaryReplyId
                    )
                );
                setReply(replyText);
            }
        });
    };

    const acceptComment = () => {
        onAccept(undefined, {
            onSuccess: () => setLocalAccepted(true)
        });
    };

    const acceptAndReply = () => {
        onAccept({ silent: true }, {
            onSuccess: () => {
                setLocalAccepted(true);
                if (reply) submitReply();
            }
        });
    };

    return (
        <Dialog
            onClose={onClose}
            maxWidth="md"
            fullWidth
            {...others}
        >
            <DialogTitle>Comentário da aula</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2}>
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            Aula: {lesson?.title || "Sem aula"} | Autor: {user?.name || "Usuario"}
                        </Typography>
                        <Typography variant="body2" color={localAccepted ? "success.main" : "warning.main"}>
                            {localAccepted ? "Aprovado" : "Pendente de aprovação"}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2">Comentário</Typography>
                        <Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {comment}
                        </Typography>
                    </Box>

                    <TextField
                        label="Editar comentário"
                        value={editComment}
                        onChange={(event) => setEditComment(event.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                    />
                    <Box display="flex" justifyContent="flex-end">
                        <Button
                            variant="contained"
                            disabled={!editComment || editLoading}
                            onClick={() => onEdit({ comment: editComment, fileId })}
                        >
                            Salvar comentário
                        </Button>
                    </Box>

                    <Divider />

                    <TextField
                        label="Responder comentário"
                        value={reply}
                        onChange={(event) => setReply(event.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                    />
                    <Box display="flex" gap={1} justifyContent="flex-end">
                        {!localAccepted && (
                            <Button
                                variant="contained"
                                color="success"
                                disabled={replyLoading}
                                onClick={acceptAndReply}
                            >
                                {reply ? "Aprovar e responder" : "Aprovar"}
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            disabled={!reply || replyLoading}
                            onClick={submitReply}
                        >
                            Responder
                        </Button>
                    </Box>

                    <Divider />

                    <Box>
                        <Typography variant="subtitle2">
                            Respostas ({localReplies?.length || 0})
                        </Typography>
                        {localReplies?.length ? (
                            localReplies.map((replyItem) => (
                                <ReplyItem
                                    key={replyItem?.id}
                                    reply={replyItem}
                                    fileId={fileId}
                                    onEdit={onEdit}
                                    onDelete={onDelete}
                                    editLoading={editLoading}
                                />
                            ))
                        ) : (
                            <Typography color="text.secondary">
                                Nenhuma resposta registrada.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                {!localAccepted && (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={acceptComment}
                    >
                        Aprovar
                    </Button>
                )}
                <Button
                    variant="contained"
                    color="error"
                    onClick={() => {
                        onDelete();
                        onClose();
                    }}
                >
                    Deletar comentário
                </Button>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}

export default CommentModal;
